import React from "react";

import { LANGUAGE_KEY } from "../constants";
import { detectLanguage, SUPPORTED_LANGUAGES } from "./detect";
import en from "./en/strings";
import es from "./es/strings";
import enLeagues from "./en/leagues";
import esLeagues from "./es/leagues";

// Per-language string bundles. `useTranslation()` returns one of these; each is
// a plain object namespaced by component/page (see en/strings.js).
var STRINGS = { en: en, es: es };

// Per-language league display-name overrides, keyed by league slug. Returned by
// `useLeagueNames()`; an entry takes precedence over the server-provided name.
var LEAGUES = { en: enLeagues, es: esLeagues };

// Mirrors the LayoutContext/GroupContext pattern: a state context holding the
// active language code and a dispatch context holding the setter.
var LanguageStateContext = React.createContext();
var LanguageDispatchContext = React.createContext();

function LanguageProvider({ children }) {
  var [language, setLanguageState] = React.useState(detectLanguage);

  // Persist the manual choice so it survives reloads (and overrides browser
  // detection from then on), then re-render consumers with the new bundle.
  function setLanguage(lang) {
    if (SUPPORTED_LANGUAGES.indexOf(lang) === -1) {
      return;
    }
    localStorage.setItem(LANGUAGE_KEY, lang);
    setLanguageState(lang);
  }

  return (
    <LanguageStateContext.Provider value={language}>
      <LanguageDispatchContext.Provider value={setLanguage}>
        {children}
      </LanguageDispatchContext.Provider>
    </LanguageStateContext.Provider>
  );
}

// { language, setLanguage } — for the header's flag switcher.
function useLanguage() {
  var language = React.useContext(LanguageStateContext);
  var setLanguage = React.useContext(LanguageDispatchContext);
  if (language === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return { language: language, setLanguage: setLanguage };
}

// The string bundle for the active language; components read `t.<ns>.<key>`.
function useTranslation() {
  var language = React.useContext(LanguageStateContext);
  if (language === undefined) {
    throw new Error("useTranslation must be used within a LanguageProvider");
  }
  return STRINGS[language] || STRINGS.en;
}

// The active language's league display-name overrides (slug -> name). Callers
// resolve a label override-first, e.g.
// `leagueOverrides[slug] || serverName || slug`.
function useLeagueNames() {
  var language = React.useContext(LanguageStateContext);
  if (language === undefined) {
    throw new Error("useLeagueNames must be used within a LanguageProvider");
  }
  return LEAGUES[language] || LEAGUES.en;
}

export { LanguageProvider, useLanguage, useTranslation, useLeagueNames };
